import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, UserX } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

export default function LegalSettings() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "legal_approver", password: "" });

  const { data: users } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: roleData } = useQuery({ queryKey: ["user-roles"], queryFn: async () => (await api.get("/users/roles")).data });
  const roles = roleData?.roles || [];

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", f)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setF({ email: "", name: "", role: "legal_approver", password: "" }); toast.success("User created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setRole = useMutation({
    mutationFn: async ({ id, role }) => (await api.put(`/users/${id}`, { role })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Role updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const deactivate = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User deactivated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="legal-settings-page">
      <PageHeader title="Legal Settings · Team & Roles" subtitle="Create legal_admin / legal_approver accounts and assign roles.">
        <Button onClick={() => setOpen(true)} data-testid="create-user-btn" className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900">
          <UserPlus className="mr-1.5 h-4 w-4" /> New User
        </Button>
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-[#1E2D4A]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E1729] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-[#1E2D4A]">
            {(users || []).map((u) => (
              <tr key={u.id} className="bg-[#111A2E]/40" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-slate-100">{u.name}</td>
                <td className="px-4 py-3 text-slate-400">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => setRole.mutate({ id: u.id, role: v })}>
                    <SelectTrigger className="h-8 w-44 border-[#2D3F62] bg-[#0B1120] text-xs text-slate-100" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">{u.active === false ? <span className="text-rose-300">Disabled</span> : <span className="flex items-center gap-1 text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" /> Active</span>}</td>
                <td className="px-4 py-3 text-right">
                  {u.active !== false && (
                    <Button size="sm" variant="outline" className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => deactivate.mutate(u.id)} data-testid={`user-deactivate-${u.id}`}>
                      <UserX className="mr-1 h-3.5 w-3.5" /> Disable
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label className="text-xs text-slate-400">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="user-name-input" /></div>
            <div><Label className="text-xs text-slate-400">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="user-email-input" /></div>
            <div>
              <Label className="text-xs text-slate-400">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-400">Temporary password (min 8 chars)</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="user-password-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !f.email || !f.name || f.password.length < 8} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="user-create-confirm">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
